/**
 * Meta OAuth Credentials
 *
 * Reads Meta (Instagram/Facebook) app credentials from system_settings table.
 * App secret is stored encrypted via lib/encryption.ts (AES-256-GCM).
 */

import type { SupabaseClient } from "@/lib/supabase"
import { decrypt } from "@/lib/encryption"

export interface MetaOAuthCredentials {
  appId: string
  appSecret: string
  webhookVerifyToken: string
  /** Facebook Login for Business configuration ID (replaces scope in OAuth URL) */
  fbLoginConfigId: string
  /** Dedicated User-token configuration for the Meta Lead Ads connection. */
  leadAdsLoginConfigId: string
  /**
   * WhatsApp Embedded Signup configuration ID (type WA_EMBEDDED_SIGNUP). Distinct
   * from `fbLoginConfigId` (that one is for Facebook page login). Empty until an
   * admin creates the Embedded Signup config in the Meta app and stores it.
   */
  whatsappConfigId: string
}

export interface InstagramOAuthCredentials {
  appId: string
  appSecret: string
}

type KVSetting = { key: string; value: string | null }

/**
 * Get Meta OAuth credentials from system settings (key/value format).
 * Decrypts the app_secret stored as AES-256-GCM ciphertext.
 */
export async function getMetaCredentials(
  supabase: SupabaseClient,
): Promise<MetaOAuthCredentials> {
  const { data, error } = await supabase
    .from("system_settings")
    .select("key,value")
    .in("key", [
      "meta_app_id",
      "meta_app_secret",
      "meta_webhook_verify_token",
      "meta_app_config_id",
      "meta_lead_ads_config_id",
      "meta_whatsapp_config_id",
    ])

  if (error) {
    throw new Error(`Failed to fetch Meta credentials: ${error.message}`)
  }

  if (!Array.isArray(data) || data.length === 0) {
    throw new Error(
      "Meta OAuth credentials not found in system_settings. Configure them in System Settings.",
    )
  }

  const map = new Map(
    (data as KVSetting[]).map((row) => [row?.key, row?.value ?? ""]),
  )

  const appId = map.get("meta_app_id") || ""
  const appSecretEnc = map.get("meta_app_secret") || ""
  const webhookVerifyToken = map.get("meta_webhook_verify_token") || ""
  const fbLoginConfigId = map.get("meta_app_config_id") || ""
  const leadAdsLoginConfigId = map.get("meta_lead_ads_config_id") || ""
  const whatsappConfigId = map.get("meta_whatsapp_config_id") || ""

  if (!appId || !appSecretEnc) {
    throw new Error(
      "Meta OAuth credentials are incomplete in system_settings. Configure App ID and App Secret in System Settings.",
    )
  }

  const appSecret = decrypt(appSecretEnc)

  if (!appSecret) {
    throw new Error(
      "Meta App Secret is empty after decryption. Re-enter the secret in System Settings.",
    )
  }

  return {
    appId,
    appSecret,
    webhookVerifyToken,
    fbLoginConfigId,
    leadAdsLoginConfigId,
    whatsappConfigId,
  }
}

/**
 * Get Instagram OAuth credentials from system settings.
 * Meta creates a separate Instagram app (-IG suffix) with its own App ID and Secret
 * for Business Login for Instagram. These are different from the main Meta App credentials.
 */
export async function getInstagramCredentials(
  supabase: SupabaseClient,
): Promise<InstagramOAuthCredentials> {
  const { data, error } = await supabase
    .from("system_settings")
    .select("key,value")
    .in("key", ["instagram_app_id", "instagram_app_secret"])

  if (error) {
    throw new Error(`Failed to fetch Instagram credentials: ${error.message}`)
  }

  if (!Array.isArray(data) || data.length === 0) {
    throw new Error(
      "Instagram OAuth credentials not found in system_settings. Configure them in System Settings.",
    )
  }

  const map = new Map(
    (data as KVSetting[]).map((row) => [row?.key, row?.value ?? ""]),
  )

  const appId = map.get("instagram_app_id") || ""
  const appSecretEnc = map.get("instagram_app_secret") || ""

  if (!appId || !appSecretEnc) {
    throw new Error(
      "Instagram OAuth credentials are incomplete. Configure Instagram App ID and App Secret in System Settings.",
    )
  }

  const appSecret = decrypt(appSecretEnc)

  if (!appSecret) {
    throw new Error(
      "Instagram App Secret is empty after decryption. Re-enter the secret in System Settings.",
    )
  }

  return { appId, appSecret }
}
